import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStaticTimelineData, getStaticGpsTrajectory } from '../services/staticData';

const TimelineView = ({ vehicle, selectedDate, onBack, onMinuteClick, onDateChange }) => {
  const navigate = useNavigate();
  const [timelineData, setTimelineData] = useState([]);
  const [gpsTrajectory, setGpsTrajectory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [error, setError] = useState('');
  const [tooltip, setTooltip] = useState({ show: false, content: '', x: 0, y: 0 });
  
  // Fallback to today's date if selectedDate is not provided
  const currentDate = selectedDate || new Date().toISOString().split('T')[0];

  useEffect(() => {
    console.log('TimelineView - useEffect triggered with:', { vehicle: !!vehicle, selectedDate, currentDate });
    if (vehicle && currentDate) {
      fetchTimelineData();
      fetchGpsTrajectory();
    }
  }, [vehicle, currentDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTimelineData = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getStaticTimelineData(vehicle.id, currentDate);
      console.log('Timeline data received (static):', data); // Debug log
      
      // Static data is already in the correct format
      if (Array.isArray(data)) {
        console.log('TimelineView - Static data is array, length:', data.length);
        setTimelineData(data);
      } else {
        console.error('Unexpected timeline data format:', data);
        setError('Invalid timeline data format');
        setTimelineData([]);
      }
    } catch (err) {
      console.error('Error fetching timeline data:', err);
      const errorMessage = err.message || 'Failed to load timeline data';
      setError(typeof errorMessage === 'string' ? errorMessage : 'Failed to load timeline data');
      setTimelineData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchGpsTrajectory = async () => {
    try {
      setGpsLoading(true);
      console.log('Fetching GPS trajectory for:', { vehicleId: vehicle.id, date: currentDate });
      const data = await getStaticGpsTrajectory(vehicle.id, currentDate);
      console.log('GPS trajectory data received (static):', data);
      
      // Static data is already in the correct format
      if (Array.isArray(data)) {
        console.log('GPS data is array, setting trajectory:', data);
        setGpsTrajectory(data);
      } else {
        console.log('No GPS trajectory data found or unexpected format:', data);
        setGpsTrajectory([]);
      }
    } catch (err) {
      console.error('Error fetching GPS trajectory:', err);
      // Don't show error for GPS data as it's optional
      setGpsTrajectory([]);
    } finally {
      setGpsLoading(false);
    }
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    // Notify parent component about date change
    if (onDateChange) {
      onDateChange(newDate);
    }
  };

  const handleMinuteClick = (minuteData) => {
    console.log('TimelineView - handleMinuteClick called with:', minuteData);
    if (minuteData.has_video) {
      onMinuteClick(minuteData);
      navigate(`/video/${vehicle.id}/${minuteData.minute}`);
    }
  };

  const handleMouseEnter = (e, minuteData, index) => {
    const rect = e.target.getBoundingClientRect();
    const content = `${formatTime(minuteData?.minute || index)} - ${minuteData?.has_video ? 'Video Available (Click to view)' : 'No Video'}`;
    setTooltip({
      show: true,
      content,
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
  };

  const handleMouseLeave = () => {
    setTooltip({ show: false, content: '', x: 0, y: 0 });
  };

  const formatTime = (minute) => {
    // Convert minute to actual time (4am = 240 minutes, 6pm = 1080 minutes)
    const actualMinute = minute + 240; // Start from 4am (240 minutes)
    const hours = Math.floor(actualMinute / 60);
    const mins = actualMinute % 60;
    
    // Convert to 12-hour format with AM/PM
    let displayHour = hours;
    let ampm = 'AM';
    
    if (hours === 0) {
      displayHour = 12;
    } else if (hours === 12) {
      ampm = 'PM';
    } else if (hours > 12) {
      displayHour = hours - 12;
      ampm = 'PM';
    }
    
    return `${displayHour}:${mins.toString().padStart(2, '0')} ${ampm}`;
  };

  const formatMinuteLabel = (minute) => {
    const mins = minute % 60;
    const hour = Math.floor(minute / 60);
    
    // Show labels every 15 minutes (4:00, 4:15, 4:30, 4:45, 5:00, 5:15, etc.)
    if (mins === 0 || mins === 15 || mins === 30 || mins === 45) {
      const actualHour = hour + 4; // Start from 4am
      let displayHour = actualHour;
      let ampm = 'AM';
      
      if (actualHour === 0) {
        displayHour = 12;
      } else if (actualHour === 12) {
        ampm = 'PM';
      } else if (actualHour > 12) {
        displayHour = actualHour - 12;
        ampm = 'PM';
      }
      
      return `${displayHour}:${mins.toString().padStart(2, '0')} ${ampm}`;
    }
    return ''; // Empty for all other minutes
  };

  if (loading) {
    return <div className="loading">Loading timeline...</div>;
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div>
          <button className="btn btn-secondary" onClick={() => navigate('/')} style={{ marginRight: '20px' }}>
            ← Back to Vehicles
          </button>
          <h1 style={{ display: 'inline' }}>{vehicle.vehicle_name}</h1>
        </div>
        <div>
          <label htmlFor="date-select" style={{ marginRight: '10px' }}>Date:</label>
          <input
            type="date"
            id="date-select"
            value={currentDate}
            onChange={handleDateChange}
            style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
          />
        </div>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="card">
        <h3 style={{ marginBottom: '20px' }}>Video Timeline - {currentDate}</h3>
        
        <div className="timeline-legend">
          <div className="timeline-legend-item">
            <div className="timeline-legend-color has-video"></div>
            <span>Video Available</span>
          </div>
          <div className="timeline-legend-item">
            <div className="timeline-legend-color no-video"></div>
            <span>No Video</span>
          </div>
          <div className="timeline-legend-item">
            <span>📅</span>
            <span>Click green bars to view videos</span>
          </div>
        </div>

        {/* GPS Trajectory Map */}
        {gpsTrajectory.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ marginBottom: '10px', color: '#333' }}>Daily GPS Trajectory</h4>
            <div className="gps-map-container" style={{ 
              height: '300px',
              borderRadius: '8px', 
              overflow: 'hidden',
              border: '1px solid #ddd',
              position: 'relative',
              backgroundColor: '#f8f9fa'
            }}>
              {(() => {
                // Calculate map bounds from GPS data
                const lats = gpsTrajectory.map(p => p.latitude);
                const lngs = gpsTrajectory.map(p => p.longitude);
                const minLat = Math.min(...lats);
                const maxLat = Math.max(...lats);
                const minLng = Math.min(...lngs);
                const maxLng = Math.max(...lngs);
                
                console.log('GPS Bounds Calculation:', {
                  lats: lats,
                  lngs: lngs,
                  minLat: minLat,
                  maxLat: maxLat,
                  minLng: minLng,
                  maxLng: maxLng
                });
                
                // Add padding to bounds
                const latPadding = (maxLat - minLat) * 0.1;
                const lngPadding = (maxLng - minLng) * 0.1;
                
                const bounds = {
                  minLat: minLat - latPadding,
                  maxLat: maxLat + latPadding,
                  minLng: minLng - lngPadding,
                  maxLng: maxLng + lngPadding
                };
                
                console.log('Final bounds with padding:', bounds);
                
                return (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    position: 'relative'
                  }}>
                    {/* OpenStreetMap with limited GPS markers */}
                    {(() => {
                      // Limit to first 10 markers to avoid URL length issues
                      const limitedMarkers = gpsTrajectory.slice(0, 10);
                      const markerUrl = limitedMarkers.map((point, index) => {
                        return `&marker=${point.latitude},${point.longitude}`;
                      }).join('');
                      
                      const fullUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${bounds.minLng},${bounds.minLat},${bounds.maxLng},${bounds.maxLat}&layer=mapnik${markerUrl}`;
                      
                      console.log('Map URL length:', fullUrl.length);
                      console.log('Number of markers:', limitedMarkers.length);
                      console.log('Full URL:', fullUrl);
                      
                      return (
                        <iframe
                          src={fullUrl}
                          width="100%"
                          height="100%"
                          style={{ 
                            border: 0,
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            zIndex: 1
                          }}
                          loading="lazy"
                          title="Daily GPS Trajectory Map"
                        />
                      );
                    })()}
                    
                    {/* GPS Info Overlay */}
                    <div style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      backgroundColor: 'rgba(0,0,0,0.8)',
                      color: 'white',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      maxWidth: '200px',
                      zIndex: 5
                    }}>
                      <div><strong>📍 GPS Points:</strong> {gpsTrajectory.length}</div>
                      <div><strong>📍 Showing:</strong> {Math.min(gpsTrajectory.length, 10)} markers</div>
                      {gpsTrajectory.length > 10 && (
                        <div style={{ color: '#ffc107', fontSize: '10px' }}>
                          ⚠️ Limited to first 10 points
                        </div>
                      )}
                      <div><strong>📍 Start:</strong> {gpsTrajectory.length > 0 ? 
                        `${gpsTrajectory[0].latitude.toFixed(4)}, ${gpsTrajectory[0].longitude.toFixed(4)}` : 'N/A'}</div>
                      <div><strong>📍 End:</strong> {gpsTrajectory.length > 1 ? 
                        `${gpsTrajectory[gpsTrajectory.length - 1].latitude.toFixed(4)}, ${gpsTrajectory[gpsTrajectory.length - 1].longitude.toFixed(4)}` : 'N/A'}</div>
                    </div>

                    {/* Legend */}
                    <div style={{
                      position: 'absolute',
                      bottom: '10px',
                      left: '10px',
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      fontSize: '10px',
                      zIndex: 5
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '8px', height: '8px', backgroundColor: '#007bff', borderRadius: '50%' }}></div>
                        <span>GPS Points</span>
                        <div style={{ width: '8px', height: '8px', backgroundColor: '#28a745', borderRadius: '50%', marginLeft: '8px' }}></div>
                        <span>Start</span>
                        <div style={{ width: '8px', height: '8px', backgroundColor: '#dc3545', borderRadius: '50%', marginLeft: '8px' }}></div>
                        <span>End</span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* GPS Loading State */}
        {gpsLoading && (
          <div style={{ 
            marginBottom: '20px', 
            padding: '20px', 
            textAlign: 'center', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px',
            color: '#666'
          }}>
            <div style={{ fontSize: '16px', marginBottom: '8px' }}>🗺️</div>
            <div>Loading GPS trajectory data...</div>
          </div>
        )}

        {/* No GPS Data Message */}
        {!gpsLoading && gpsTrajectory.length === 0 && (
          <div style={{ 
            marginBottom: '20px', 
            padding: '20px', 
            textAlign: 'center', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px',
            color: '#666',
            border: '1px solid #e9ecef'
          }}>
            <div style={{ fontSize: '16px', marginBottom: '8px' }}>🗺️</div>
            <div>No GPS trajectory data available for this date</div>
            <div style={{ fontSize: '12px', marginTop: '4px', color: '#999' }}>
              GPS data may not be available for this vehicle/date
            </div>
          </div>
        )}

        <div className="timeline-container">
          {Array.isArray(timelineData) && timelineData.length > 0 ? (
            <div className="timeline-scroll">
              <div className="timeline-content">
                {/* Timeline bars with hour markers */}
                <div className="timeline-items">
                  {timelineData.map((minuteData, index) => {
                    const hourLabel = formatMinuteLabel(minuteData?.minute || index);
                    // Debug logging for each timeline item
                    if (index < 5) { // Only log first 5 items to avoid spam
                      console.log('TimelineView - Rendering timeline item:', index, minuteData);
                    }
                    return (
                      <div
                        key={minuteData?.minute || index}
                        className={`timeline-item ${minuteData?.has_video ? 'has-video' : 'no-video'}`}
                        onClick={() => handleMinuteClick(minuteData)}
                        onMouseEnter={(e) => handleMouseEnter(e, minuteData, index)}
                        onMouseLeave={handleMouseLeave}
                        style={{ 
                          cursor: minuteData?.has_video ? 'pointer' : 'default',
                          opacity: minuteData?.has_video ? 1 : 0.6,
                          position: 'relative'
                        }}
                        title={hourLabel ? `${hourLabel} - ${minuteData?.has_video ? 'Video Available' : 'No Video'}` : undefined}
                      >
                        {/* Hour marker line */}
                        {hourLabel && (
                          <div style={{
                            position: 'absolute',
                            bottom: '-20px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            fontSize: '10px',
                            color: '#007bff',
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                            zIndex: 10
                          }}>
                            {hourLabel}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>


              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <div style={{ fontSize: '18px', marginBottom: '10px' }}>📹</div>
              <div>No timeline data available for this date</div>
              <div style={{ fontSize: '12px', marginTop: '5px', color: '#999' }}>
                Try selecting a different date
              </div>
            </div>
          )}
        </div>

        <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '6px', fontSize: '13px', color: '#666' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <div>
              <strong>Timeline Summary (4:00 AM - 6:00 PM)</strong>
            </div>
            <div style={{ fontSize: '11px', color: '#999' }}>
              📊 Scroll horizontally to see more
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
            <div>
              <strong>Total Minutes:</strong> {Array.isArray(timelineData) ? timelineData.length : 0}
            </div>
            <div>
              <strong>Video Available:</strong> {Array.isArray(timelineData) ? timelineData.filter(m => m?.has_video).length : 0}
            </div>
            <div>
              <strong>Coverage:</strong> {Array.isArray(timelineData) && timelineData.length > 0 ? 
                Math.round((timelineData.filter(m => m?.has_video).length / timelineData.length) * 100) : 0}%
            </div>
            <div>
              <strong>Time Range:</strong> {Array.isArray(timelineData) && timelineData.length > 0 ? 
                `${formatTime(timelineData[0]?.minute || 0)} - ${formatTime(timelineData[timelineData.length - 1]?.minute || 0)}` : '4:00 AM - 6:00 PM'}
            </div>
            <div>
              <strong>GPS Points:</strong> {gpsTrajectory.length}
            </div>
            <div>
              <strong>GPS Status:</strong> {gpsTrajectory.length > 0 ? '✅ Available' : '❌ No Data'}
            </div>
          </div>
        </div>
      </div>

      {/* Custom Tooltip */}
      {tooltip.show && (
        <div
          style={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translateX(-50%)',
            background: 'rgba(0, 0, 0, 0.95)',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: '500',
            zIndex: 999999,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
            pointerEvents: 'none',
            whiteSpace: 'nowrap'
          }}
        >
          {tooltip.content}
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              border: '6px solid transparent',
              borderTopColor: 'rgba(0, 0, 0, 0.95)'
            }}
          />
        </div>
      )}
    </div>
  );
};

export default TimelineView;
